/**
 * Currency Utility Functions
 * Centralized currency formatting for international support
 */

// Supported currencies with symbols and locale defaults
export const CURRENCIES = {
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', locale: 'de-DE' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
  CHF: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH' },
  HRK: { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna', locale: 'hr-HR' },
  CZK: { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', locale: 'cs-CZ' },
  PLN: { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', locale: 'pl-PL' },
  HUF: { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint', locale: 'hu-HU' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', locale: 'sv-SE' },
  NOK: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', locale: 'nb-NO' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone', locale: 'da-DK' },
  RON: { code: 'RON', symbol: 'lei', name: 'Romanian Leu', locale: 'ro-RO' },
  BGN: { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev', locale: 'bg-BG' },
  TRY: { code: 'TRY', symbol: '₺', name: 'Turkish Lira', locale: 'tr-TR' },
  RSD: { code: 'RSD', symbol: 'din', name: 'Serbian Dinar', locale: 'sr-Latn-RS' },
  BAM: { code: 'BAM', symbol: 'KM', name: 'Convertible Mark', locale: 'bs-BA' },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP' },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN' },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
  BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
  MXN: { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', locale: 'es-MX' },
  THB: { code: 'THB', symbol: '฿', name: 'Thai Baht', locale: 'th-TH' },
  IDR: { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', locale: 'id-ID' },
  MYR: { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', locale: 'ms-MY' },
  ZAR: { code: 'ZAR', symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  ILS: { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', locale: 'he-IL' },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE' },
  NZD: { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', locale: 'en-NZ' },
} as const;

export type CurrencyCode = keyof typeof CURRENCIES;

// Default currency
const DEFAULT_CURRENCY: CurrencyCode = 'EUR';

/**
 * Format a price amount with the correct currency symbol and locale formatting.
 * Uses Intl.NumberFormat for proper locale-aware formatting.
 *
 * @param amount - The price amount (NOT in cents, actual value)
 * @param currency - Currency code (EUR, USD, GBP, etc.)
 * @param options - Formatting options
 */
export function formatCurrency(
  amount: number,
  currency: CurrencyCode | string = DEFAULT_CURRENCY,
  options: {
    decimals?: number;
    compact?: boolean;
  } = {}
): string {
  const currencyInfo = CURRENCIES[currency as CurrencyCode];
  const locale = currencyInfo?.locale || 'en-US';
  const { decimals, compact = false } = options;

  try {
    const minDecimals = decimals !== undefined ? decimals : (amount % 1 === 0 ? 0 : 2);
    const maxDecimals = decimals !== undefined ? decimals : 2;

    if (compact) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(amount);
    }

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    }).format(amount);
  } catch {
    // Fallback for unsupported currencies
    const symbol = currencyInfo?.symbol || currency;
    return `${symbol}${amount.toFixed(decimals ?? 2)}`;
  }
}

/**
 * Get just the currency symbol for a given currency code.
 */
export function getCurrencySymbol(currency: CurrencyCode | string = DEFAULT_CURRENCY): string {
  return CURRENCIES[currency as CurrencyCode]?.symbol || currency;
}

/**
 * Format price from cents (for Stripe) to display string.
 */
export function formatPriceFromCents(cents: number, currency: CurrencyCode | string = DEFAULT_CURRENCY): string {
  return formatCurrency(cents / 100, currency);
}

/**
 * Get currency options for dropdowns, sorted by relevance.
 */
export function getCurrencyOptions(): Array<{ code: CurrencyCode; label: string; symbol: string }> {
  // Priority order: EUR first, then major currencies, then alphabetical
  const priority: CurrencyCode[] = [
    'EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'NZD',
    'SEK', 'NOK', 'DKK', 'CZK', 'PLN', 'HUF', 'RON', 'BGN',
    'HRK', 'RSD', 'BAM', 'TRY',
    'JPY', 'CNY', 'INR', 'BRL', 'MXN', 'THB', 'IDR', 'MYR',
    'ZAR', 'ILS', 'AED',
  ];

  return priority.map(code => ({
    code,
    label: `${CURRENCIES[code].name} (${CURRENCIES[code].symbol})`,
    symbol: CURRENCIES[code].symbol,
  }));
}
